import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

interface FilterRow {
  path: string;
  operator: string;
  valueType: string;
  value: string;
}

@Component({
  selector: 'app-filter-editor',
  templateUrl: './filter-editor.component.html',
  styleUrls: ['./filter-editor.component.scss']
})
export class FilterEditorComponent implements OnChanges {

  @Input() filters: any;
  @Output() filtersChange = new EventEmitter<any>();

  operators = [
    { key: 'eq', name: 'equals' },
    { key: 'ne', name: 'does not equal' },
    { key: 'in', name: 'is any of' },
    { key: 'nin', name: 'is none of' },
    { key: 'gt', name: 'is greater than' },
    { key: 'gte', name: 'is at least' },
    { key: 'lt', name: 'is less than' },
    { key: 'lte', name: 'is at most' },
    { key: 'exists', name: 'exists' },
    { key: 'custom', name: 'custom (JSON)' }
  ];

  valueTypes = ['string', 'number', 'boolean', 'null'];

  rows: FilterRow[] = [];

  private lastEmitted: any;

  ngOnChanges(changes: SimpleChanges): void {
    // Only re-parse when the change came from outside, not from our own emit
    if (changes.filters && changes.filters.currentValue !== this.lastEmitted) {
      this.parseFilters();
    }
  }

  parseFilters() {
    this.rows = [];

    if (!this.filters || typeof this.filters !== 'object') {
      return;
    }

    for (const path of Object.keys(this.filters)) {
      const value = this.filters[path];
      this.rows.push(this.parseEntry(path, value));
    }
  }

  parseEntry(path: string, value: any): FilterRow {
    if (value === null || typeof value !== 'object') {
      return {
        path,
        operator: 'eq',
        valueType: this.typeOf(value),
        value: this.displayValue(value)
      };
    }

    const keys = Object.keys(value);

    if (keys.length === 1) {
      const operatorKey = keys[0];
      const operatorValue = value[operatorKey];

      if (operatorKey === '$exists') {
        return { path, operator: 'exists', valueType: 'boolean', value: String(operatorValue) };
      }

      if (['$ne', '$gt', '$gte', '$lt', '$lte'].indexOf(operatorKey) !== -1
        && (operatorValue === null || typeof operatorValue !== 'object')) {
        return {
          path,
          operator: operatorKey.substring(1),
          valueType: this.typeOf(operatorValue),
          value: this.displayValue(operatorValue)
        };
      }

      if (['$in', '$nin'].indexOf(operatorKey) !== -1 && Array.isArray(operatorValue)
        && operatorValue.every(item => item === null || typeof item !== 'object')) {
        return {
          path,
          operator: operatorKey.substring(1),
          valueType: this.typeOf(operatorValue.length > 0 ? operatorValue[0] : ''),
          value: operatorValue.map(item => this.displayValue(item)).join(', ')
        };
      }
    }

    return { path, operator: 'custom', valueType: 'string', value: JSON.stringify(value) };
  }

  typeOf(value: any): string {
    if (value === null) {
      return 'null';
    }
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'boolean') {
      return 'boolean';
    }
    return 'string';
  }

  displayValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  castValue(value: string, valueType: string): any {
    switch (valueType) {
      case 'number':
        return parseFloat(value);
      case 'boolean':
        return value.trim().toLowerCase() === 'true';
      case 'null':
        return null;
      default:
        return value;
    }
  }

  addRow() {
    this.rows.push({ path: '', operator: 'eq', valueType: 'string', value: '' });
  }

  removeRow(index: number) {
    this.rows.splice(index, 1);
    this.emitFilters();
  }

  emitFilters() {
    const filters: any = {};

    for (const row of this.rows) {
      if (!row.path) {
        continue;
      }

      switch (row.operator) {
        case 'eq':
          filters[row.path] = this.castValue(row.value, row.valueType);
          break;
        case 'exists':
          filters[row.path] = { $exists: row.value.trim().toLowerCase() === 'true' };
          break;
        case 'in':
        case 'nin':
          filters[row.path] = {
            ['$' + row.operator]: row.value.split(',')
              .map(item => item.trim())
              .filter(item => item !== '')
              .map(item => this.castValue(item, row.valueType))
          };
          break;
        case 'custom':
          try {
            filters[row.path] = JSON.parse(row.value);
          } catch (err) {
            // Invalid JSON, skip this row until it parses
          }
          break;
        default:
          filters[row.path] = { ['$' + row.operator]: this.castValue(row.value, row.valueType) };
      }
    }

    this.filters = filters;
    this.lastEmitted = filters;
    this.filtersChange.emit(filters);
  }
}
